import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield,
  Package,
  FileText,
  Award,
  AlertTriangle,
  LogOut,
  ArrowRight,
  ClipboardCheck,
  ClipboardList,
  Users,
  Calendar,
  GraduationCap,
  FileWarning,
  Lock,
  TrendingUp,
  Calculator,
  Truck,
  Bell,
  FolderKanban
} from 'lucide-react';
import { api, assetApi, certificationApi, dotApi } from '@/utils/api';
import { billingApi } from '@/utils/billingApi';
import { canSeeTile, canCreatePlans, type TileKey } from '@/utils/roleGate';
import ReadOnlyBanner from '@/components/ReadOnlyBanner';
import { ComingSoonInterest } from '@/components/ComingSoonInterest';

export function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState({
    totalAssets: 0,
    expiring: 0,
    damaged: 0,
    expiringCerts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [readOnlyReason, setReadOnlyReason] = useState<string | null>(null);
  // U4 (WD-T4): the DOT tile is assignment-driven — ≥1 active vehicle
  // assignment (dot_vehicle_assignments), never a ladder-tier guess.
  const [isAssignedDriver, setIsAssignedDriver] = useState(false);

  useEffect(() => {
    // Mount the read-only gate banner (day-15 lock) from the billing status.
    billingApi.status()
      .then((s: any) => setReadOnlyReason(s?.readOnly ? (s.readOnlyReason || 'Billing action required') : null))
      .catch(() => { /* billing status failure shouldn't block the dashboard */ });
  }, []);

  useEffect(() => {
    // U4: DOT tile visibility = the user has ≥1 active vehicle assignment.
    // Designated drivers (DotDriver rows) hold FLEET_ASSIGNMENT_READ self-scoped
    // server-side; anyone else gets an empty/failed read → tile hidden. Fail
    // closed to "not a driver" — a fetch hiccup must never surface a dead tile.
    dotApi.listAssignments()
      .then((res: any) => setIsAssignedDriver((res?.data || []).some((a: any) => !a.revokedAt)))
      .catch(() => setIsAssignedDriver(false));
  }, []);

  useEffect(() => {
    // Seed from localStorage for instant paint, then refresh from the server so
    // the greeting always reflects profile edits (stale cache said "Demo").
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    setUser(JSON.parse(userData));

    api.get('/auth/profile').then((res: any) => {
      const fresh = res.data ?? res;
      if (fresh?.id) {
        setUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
      }
    }).catch(() => { /* keep cached user; /auth/me hiccup shouldn't blank the dashboard */ });

    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const [assetsResponse, expiringResponse, certsResponse] = await Promise.all([
        assetApi.getAll(),
        assetApi.getExpiring(90),
        certificationApi.getExpiring(90),
      ]);

      setStats({
        totalAssets: assetsResponse.data.length,
        expiring: expiringResponse.data.length,
        damaged: assetsResponse.data.filter((a: any) => a.status === 'DAMAGED').length,
        expiringCerts: certsResponse.data.length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Role-gate helper (docs/ROLE-MATRIX-v1.md) — client-side UX truth only;
  // server enforcement remains authoritative for hidden-but-reachable routes.
  const can = (tile: TileKey) => canSeeTile(user?.role, tile);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <ReadOnlyBanner reason={readOnlyReason} />
      </div>
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-[1fr_auto] sm:grid-cols-3 items-center gap-x-3 gap-y-2">
          <div className="flex items-center space-x-2 justify-self-start">
            <img src="/logo.svg" alt="Conformara logo" className="h-9 w-9" />
            <span className="text-2xl font-bold text-gray-900">Conformara</span>
          </div>
          <div className="row-start-2 col-span-2 sm:row-start-auto sm:col-span-1 sm:col-start-2 justify-self-start sm:justify-self-center sm:text-center min-w-0">
            <Link to="/profile" className="text-sm text-gray-600 hover:text-blue-600 transition block truncate">
              {user?.firstName} {user?.lastName}
            </Link>
            <p className="text-xs text-gray-400 leading-tight">{user?.role}</p>
          </div>
          <div className="row-start-1 col-start-2 sm:col-start-3 justify-self-end">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* UAT-002 (user-POV 2026-10-06): a brand-new trial landed on a dense
            module grid with no guidance. When the tenant is empty, show a
            lightweight getting-started checklist ABOVE the tiles — 3 concrete
            next steps with links, filtered by what this role can see. */}
        {!loading && stats.totalAssets === 0 && stats.expiringCerts === 0 && (
          <div data-testid="onboarding-checklist" className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Getting started — set up in 3 steps
            </h2>
            <p className="text-sm text-gray-600 mt-1">Your account is brand new, so these are the first things to set up. Everything below stays available from the tiles too.</p>
            <ol className="mt-4 grid md:grid-cols-3 gap-3 list-none">
              {can('TEAM') && (
                <li className="bg-white rounded-lg border border-blue-100 p-4">
                  <div className="text-sm font-semibold text-gray-900">1. Add your team</div>
                  <p className="text-xs text-gray-500 mt-1">Create your people and field crews so projects and inspections can be assigned.</p>
                  <Link to="/team" className="mt-2 inline-block text-xs font-semibold text-blue-700 hover:underline">Go to Team →</Link>
                </li>
              )}
              <li className="bg-white rounded-lg border border-blue-100 p-4">
                <div className="text-sm font-semibold text-gray-900">{can('TEAM') ? '2' : '1'}. Track your equipment</div>
                <p className="text-xs text-gray-500 mt-1">Add your PPE and rigging gear so daily inspections are recordable.</p>
                <Link to="/assets" className="mt-2 inline-block text-xs font-semibold text-blue-700 hover:underline">Go to Asset Tracker →</Link>
              </li>
              <li className="bg-white rounded-lg border border-blue-100 p-4">
                <div className="text-sm font-semibold text-gray-900">{can('TEAM') ? '3' : '2'}. Create your first JHA</div>
                <p className="text-xs text-gray-500 mt-1">Run a pre-work hazard assessment — the core daily safety record.</p>
                <Link to="/jha/create" className="mt-2 inline-block text-xs font-semibold text-blue-700 hover:underline">Create JHA →</Link>
              </li>
            </ol>
          </div>
        )}

        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.firstName || 'User'}</h1>
          <p className="text-gray-600 mt-1">Here's what needs your attention today</p>
        </div>

        {/* Alert Stats - Compact Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/assets" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-l-4 border-blue-500">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.totalAssets}</div>
                <p className="text-xs text-gray-500">Total Assets</p>
              </div>
            </div>
          </Link>

          <Link to="/assets?filter=expiring" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-l-4 border-yellow-500">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.expiring}</div>
                <p className="text-xs text-gray-500">Assets Expiring</p>
              </div>
            </div>
          </Link>

          <Link to="/assets?filter=damaged" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-l-4 border-red-500">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.damaged}</div>
                <p className="text-xs text-gray-500">Damaged</p>
              </div>
            </div>
          </Link>

          <Link to="/certifications?filter=expiring" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-l-4 border-purple-500">
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-purple-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.expiringCerts}</div>
                <p className="text-xs text-gray-500">Certs Expiring</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Section Label */}
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Daily Actions
        </h2>

        {/* Primary Actions - Daily Use (All Consistent Gradient Style) */}
        <div className="grid md:grid-cols-4 gap-5 mb-8">
          <Link
            to="/jha/create"
            className="bg-gradient-to-br from-blue-600 to-blue-700 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <ClipboardCheck className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">Create JHA</h3>
            <p className="text-blue-200 text-sm">Pre-work hazard assessment</p>
          </Link>

          {canCreatePlans(user?.role) && <Link
            to="/construction-plans/create"
            className="bg-gradient-to-br from-orange-500 to-orange-600 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <FileText className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">Construction Plan</h3>
            <p className="text-orange-200 text-sm">ANSI A10.48 rigging plan</p>
          </Link>}

          <Link
            to="/ppe-inspection"
            className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <Shield className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">PPE Inspection</h3>
            <p className="text-green-200 text-sm">Daily equipment check</p>
          </Link>

          {can('NEW_AUDIT') && <Link
            to="/safety-audits/create"
            className="bg-gradient-to-br from-teal-500 to-teal-600 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <ClipboardList className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">New Audit</h3>
            <p className="text-teal-200 text-sm">Safety & quality inspections</p>
          </Link>}

          <Link
            to="/initial-reports"
            className="bg-gradient-to-br from-red-600 to-red-700 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <FileWarning className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">Report Incident</h3>
            <p className="text-red-200 text-sm">Initial report · voice first</p>
          </Link>
          {can('INVESTIGATIONS') && <Link
            to="/initial-reports/queue"
            className="bg-gradient-to-br from-purple-600 to-purple-700 p-5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all group text-white"
          >
            <FileWarning className="h-10 w-10 mb-3 opacity-90" />
            <h3 className="text-lg font-bold mb-1">Investigations</h3>
            <p className="text-purple-200 text-sm">Review incident workspaces</p>
          </Link>}
        </div>

        {/* Section Label */}
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Package className="h-5 w-5" />
          Management & Records
        </h2>

        {/* Management Tiles - Consistent White Style */}
        <div className="grid md:grid-cols-4 gap-5 mb-8">
          <Link
            to="/jha"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-blue-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <ClipboardCheck className="h-8 w-8 text-blue-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-blue-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">View JHAs</h3>
            <p className="text-sm text-gray-500">All hazard assessments</p>
          </Link>

          <Link
            to="/construction-plans"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-orange-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <FileText className="h-8 w-8 text-orange-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-orange-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Construction Plans</h3>
            <p className="text-sm text-gray-500">Rigging & lift plans</p>
          </Link>

          {can('SAFETY_AUDITS') && (
            <Link
            to="/safety-audits"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-teal-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <ClipboardList className="h-8 w-8 text-teal-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-teal-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Safety Audits</h3>
            <p className="text-sm text-gray-500">Inspections & findings</p>
          </Link>
          )}

          <Link
            to="/assets"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-sky-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Package className="h-8 w-8 text-sky-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-sky-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Asset Tracker</h3>
            <p className="text-sm text-gray-500">Equipment & inventory</p>
          </Link>

          <Link
            to="/certifications"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-purple-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Award className="h-8 w-8 text-purple-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-purple-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Certifications</h3>
            <p className="text-sm text-gray-500">Crew Cert Share™</p>
          </Link>

          <Link
            to="/projects"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-blue-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <FolderKanban className="h-8 w-8 text-blue-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-blue-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Projects</h3>
            <p className="text-sm text-gray-500">Sites, crews & documents</p>
          </Link>

          {can('MY_JOBS') && <Link
            to="/my-jobs"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-green-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <FolderKanban className="h-8 w-8 text-green-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-green-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">My Jobs</h3>
            <p className="text-sm text-gray-500">Assigned project packets</p>
          </Link>}

          {can('WEEKLY_ACTIVITY') && <Link
            to="/weekly-activity"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-indigo-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Calendar className="h-8 w-8 text-indigo-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-indigo-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Weekly Activity</h3>
            <p className="text-sm text-gray-500">Weekly safety report — audits, investigations & meetings</p>
          </Link>}

          {can('TEAM_DASHBOARD') && <Link
            to="/team-activity"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-violet-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Users className="h-8 w-8 text-violet-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-violet-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Team Dashboard</h3>
            <p className="text-sm text-gray-500">Manager reports</p>
          </Link>}

          {can('ANALYTICS') && (
            <Link
            to="/safety-audits/analytics"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-emerald-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="h-8 w-8 text-emerald-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-emerald-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Analytics</h3>
            <p className="text-sm text-gray-500">Audit metrics & trends</p>
          </Link>
          )}

          {can('DOT_INSPECTION') && isAssignedDriver && <Link
            to="/dot-inspection"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-cyan-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Truck className="h-8 w-8 text-cyan-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-cyan-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">DOT Inspection</h3>
            <p className="text-sm text-gray-500">eDVIR pre/post-trip</p>
          </Link>}

          {can('AUDIT_QUESTIONS') && <Link
            to="/safety-audits/templates"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-slate-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <ClipboardCheck className="h-8 w-8 text-slate-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-slate-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Audit Questions</h3>
            <p className="text-sm text-gray-500">Edit audit checklists & templates</p>
          </Link>}

          {can('DOCUMENT_LIBRARY') && <Link
            to="/policy-documents"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-emerald-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <FolderKanban className="h-8 w-8 text-emerald-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-emerald-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Document Library</h3>
            <p className="text-sm text-gray-500">Company policy documents</p>
          </Link>}

          {can('TEAM') && <Link
            to="/team"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-violet-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Users className="h-8 w-8 text-violet-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-violet-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Team</h3>
            <p className="text-sm text-gray-500">Add & manage personnel</p>
          </Link>}

          {can('FLEET') && <Link
            to="/fleet"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-cyan-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Truck className="h-8 w-8 text-cyan-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-cyan-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Fleet</h3>
            <p className="text-sm text-gray-500">Company vehicles</p>
          </Link>}

          <Link
            to="/notifications"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-amber-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Bell className="h-8 w-8 text-amber-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-amber-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Notifications</h3>
            <p className="text-sm text-gray-500">Turn on alerts on this device</p>
          </Link>

          {can('PERMISSIONS_MATRIX') && <Link
            to="/permissions"
            className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:border-slate-300 transition-all border border-gray-100 group"
          >
            <div className="flex items-center justify-between mb-3">
              <Lock className="h-8 w-8 text-slate-600" />
              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-slate-600 transition" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Permissions Matrix</h3>
            <p className="text-sm text-gray-500">Role & permission settings</p>
          </Link>}
        </div>

          {can('BILLING') && <div className="mb-6"><Link to="/billing" className="text-blue-700 underline">Billing and subscription</Link></div>}

        {/* Coming Soon section — PM + ADMIN only (ROLE-MATRIX-v1) */}
        {can('COMING_SOON') && <>
        {/* Section Label */}
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Coming Soon
        </h2>

        {/* Coming Soon Tiles */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-5 rounded-xl border-2 border-dashed border-gray-300 relative overflow-hidden">
            <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              Coming Soon
            </div>
            <GraduationCap className="h-10 w-10 text-gray-400 mb-3" />
            <h3 className="font-bold text-gray-700 mb-1">Learning Management (LMS)</h3>
            <p className="text-sm text-gray-500 mb-3">Online training courses, hands-on class scheduling, and automatic certification tracking</p>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Virtual training courses</li>
              <li>• Hands-on class scheduling</li>
              <li>• Auto-generate certifications</li>
              <li>• Expiration tracking & renewals</li>
            </ul>
            <ComingSoonInterest module="LMS" />
          </div>

          <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-5 rounded-xl border-2 border-dashed border-gray-300 relative overflow-hidden">
            <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              Coming Soon
            </div>
            <FileWarning className="h-10 w-10 text-gray-400 mb-3" />
            <h3 className="font-bold text-gray-700 mb-1">Claims Management & OSHA 300/300A</h3>
            <p className="text-sm text-gray-500 mb-3">Manage workers' compensation claims and OSHA recordkeeping</p>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Claims tracking & documentation</li>
              <li>• Claim status & follow-up history</li>
              <li>• OSHA 300 log & 300A summary</li>
              <li>• Recordkeeping exports</li>
            </ul>
            <ComingSoonInterest module="CLAIMS_OSHA" />
          </div>

          <Link
            to="/rigging-calculator"
            className="bg-gradient-to-br from-gray-100 to-gray-50 p-5 rounded-xl border-2 border-dashed border-gray-300 relative overflow-hidden hover:border-orange-300 hover:shadow transition"
          >
            <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              Coming Soon
            </div>
            <Calculator className="h-10 w-10 text-gray-400 mb-3" />
            <h3 className="font-bold text-gray-700 mb-1">Rigging Calculator</h3>
            <p className="text-sm text-gray-500 mb-3">Tower rigging force calculations with visual diagrams</p>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Slings, SWL & load calculations</li>
              <li>• Tower diagrams & force visualization</li>
              <li>• WLL percentage & safety status</li>
              <li>• In validation — vetted with owners</li>
            </ul>
            <ComingSoonInterest module="RIGGING_CALCULATOR" />
          </Link>
        </div>
        </>}
      </main>
    </div>
  );
}


